import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  useTheme,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import { Close as CloseIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { AppUser } from '../../types';

export interface OtherCompanyUser {
  rfid: string;
  name: string;
  companyName: string;
}

interface NewCompanyModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (companyData: CompanyFormData) => void;
  initialData?: CompanyFormData;
  isLoading?: boolean;
  mode?: 'create' | 'edit';
  /** Current app users for the company being edited (displayed as read-only chips) */
  appUsers?: AppUser[];
  /** App users from all other non-Annet companies (for the move-user dropdown) */
  otherCompanyUsers?: OtherCompanyUser[];
  /** Called when the admin picks a user to move into this company */
  onMoveUser?: (rfid: string) => Promise<void>;
  isMoveLoading?: boolean;
  moveError?: string | null;
  /** Non-fatal warning returned by the API (e.g. Firebase update pending) */
  moveWarning?: string | null;
}

export interface CompanyFormData {
  company_name: string;
  charging_price: number;
  rfid: string[];
  evcc_id: string[];
  pin_code: string[];
}

const emptyFormData: CompanyFormData = {
  company_name: '',
  charging_price: 0,
  rfid: [],
  evcc_id: [],
  pin_code: [],
};

const NewCompanyModal: React.FC<NewCompanyModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  isLoading = false,
  mode = 'create',
  appUsers = [],
  otherCompanyUsers = [],
  onMoveUser,
  isMoveLoading = false,
  moveError = null,
  moveWarning = null,
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState<CompanyFormData>(emptyFormData);

  const [chargingPriceInput, setChargingPriceInput] = useState<string>('');
  const [rfidInput, setRfidInput] = useState<string>('');
  const [evccIdInput, setEvccIdInput] = useState<string>('');
  const [pinCodeInput, setPinCodeInput] = useState<string>('');
  const [selectedMoveRfid, setSelectedMoveRfid] = useState<string>('');

  const [errors, setErrors] = useState<Partial<Record<keyof CompanyFormData, string>>>({});

  const parseListInput = (value: string) =>
    value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);

  useEffect(() => {
    if (!open) return;

    const nextFormData = initialData || emptyFormData;
    setFormData(nextFormData);
    setChargingPriceInput(nextFormData.charging_price.toString());
    setRfidInput(nextFormData.rfid.join(', '));
    setEvccIdInput(nextFormData.evcc_id.join(', '));
    setPinCodeInput(nextFormData.pin_code.join(', '));
    setSelectedMoveRfid('');
    setErrors({});
  }, [initialData, open]);

  const handleInputChange = (field: keyof CompanyFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (field === 'charging_price') {
      const inputValue = event.target.value;
      setChargingPriceInput(inputValue);
      
      if (inputValue === '' || inputValue === ',') {
        setFormData(prev => ({
          ...prev,
          charging_price: 0
        }));
      } else {
        const normalizedValue = inputValue.replace(',', '.');
        const numericValue = parseFloat(normalizedValue);
        if (!isNaN(numericValue)) {
          setFormData(prev => ({
            ...prev,
            charging_price: numericValue
          }));
        }
      }
    } else if (field === 'rfid') {
      const inputValue = event.target.value;
      setRfidInput(inputValue);
      setFormData(prev => ({
        ...prev,
        rfid: parseListInput(inputValue)
      }));
    } else if (field === 'evcc_id') {
      const inputValue = event.target.value;
      setEvccIdInput(inputValue);
      setFormData(prev => ({
        ...prev,
        evcc_id: parseListInput(inputValue)
      }));
    } else if (field === 'pin_code') {
      if (mode === 'edit') return;
      const inputValue = event.target.value;
      setPinCodeInput(inputValue);
      setFormData(prev => ({
        ...prev,
        pin_code: parseListInput(inputValue)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: event.target.value
      }));
    }
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CompanyFormData, string>> = {};
    
    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }
    if (formData.charging_price < 0) {
      newErrors.charging_price = 'Charging price cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleClose = () => {
    setFormData(emptyFormData);
    setChargingPriceInput('');
    setRfidInput('');
    setEvccIdInput('');
    setPinCodeInput('');
    setSelectedMoveRfid('');
    setErrors({});
    onClose();
  };

  const handleMoveUser = async () => {
    if (!selectedMoveRfid || !onMoveUser) return;
    await onMoveUser(selectedMoveRfid);
    setSelectedMoveRfid('');
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 3, 
        pb: 2,
        backgroundColor: '#FAFAFA',
        borderBottom: '1px solid #E0E0E0'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontFamily: 'Montserrat, sans-serif'
          }}>
            {mode === 'edit' ? 'Edit Company' : 'Create New Company'}
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{ color: theme.palette.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Company Name"
              value={formData.company_name}
              onChange={handleInputChange('company_name')}
              error={!!errors.company_name}
              helperText={errors.company_name}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Charging Price"
              type="text"
              value={chargingPriceInput}
              onChange={handleInputChange('charging_price')}
              error={!!errors.charging_price}
              helperText={errors.charging_price}
              placeholder="0.00"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="RFID"
              value={rfidInput}
              onChange={handleInputChange('rfid')}
              error={!!errors.rfid}
              helperText={errors.rfid || 'Separate multiple RFID values with commas'}
              placeholder="RFID-001, RFID-002"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />
          </Grid>

          {/* Current app users (edit mode only) */}
          {mode === 'edit' && appUsers.length > 0 && (
            <Grid item xs={12}>
              <Typography sx={{
                fontFamily: 'Montserrat',
                fontSize: '12px',
                fontWeight: 600,
                color: 'rgba(0,0,0,0.45)',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                mb: 1,
              }}>
                App Users
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {appUsers.map((u) => (
                  <Chip
                    key={u.rfid}
                    label={u.name}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(123, 31, 162, 0.10)',
                      color: '#7B1FA2',
                      border: '1px solid rgba(123, 31, 162, 0.28)',
                      fontFamily: 'Montserrat',
                      fontSize: '11px',
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="EVCC ID"
              value={evccIdInput}
              onChange={handleInputChange('evcc_id')}
              error={!!errors.evcc_id}
              helperText={errors.evcc_id || 'Separate multiple EVCC IDs with commas'}
              placeholder="EVCC-001, EVCC-002"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Access code"
              value={pinCodeInput}
              onChange={handleInputChange('pin_code')}
              error={!!errors.pin_code}
              disabled={mode === 'edit'}
              helperText={
                errors.pin_code ||
                (mode === 'edit'
                  ? 'Access codes cannot be edited or removed'
                  : 'Separate multiple access codes with commas')
              }
              placeholder="1234, 5678"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />
          </Grid>

          {/* Move user from another company (edit mode, non-Annet target only) */}
          {mode === 'edit' && onMoveUser && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ mt: 1 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'rgba(0,0,0,0.6)',
                  mb: 1.5,
                }}>
                  Move a user from another company
                </Typography>

                {moveError && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                    {moveError}
                  </Alert>
                )}

                {moveWarning && !moveError && (
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
                    {moveWarning}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontFamily: 'Montserrat', fontSize: '14px' }}>
                      Select user to move here
                    </InputLabel>
                    <Select
                      value={selectedMoveRfid}
                      label="Select user to move here"
                      onChange={(e) => setSelectedMoveRfid(e.target.value)}
                      disabled={isMoveLoading || otherCompanyUsers.length === 0}
                      sx={{
                        borderRadius: '8px',
                        fontFamily: 'Montserrat',
                        fontSize: '14px',
                      }}
                    >
                      {otherCompanyUsers.length === 0 && (
                        <MenuItem disabled value="">
                          No users available from other companies
                        </MenuItem>
                      )}
                      {otherCompanyUsers.map((u) => (
                        <MenuItem key={u.rfid} value={u.rfid} sx={{ fontFamily: 'Montserrat', fontSize: '14px' }}>
                          {u.name}
                          <Typography
                            component="span"
                            sx={{ color: 'rgba(0,0,0,0.45)', ml: 0.75, fontSize: '12px', fontFamily: 'Montserrat' }}
                          >
                            — {u.companyName}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    variant="outlined"
                    onClick={handleMoveUser}
                    disabled={!selectedMoveRfid || isMoveLoading}
                    startIcon={isMoveLoading ? <CircularProgress size={14} /> : <PersonAddIcon />}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontFamily: 'Montserrat',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      borderColor: '#337A5C',
                      color: '#337A5C',
                      '&:hover': { borderColor: '#2a6350', backgroundColor: 'rgba(51,122,92,0.06)' },
                      minWidth: '110px',
                      height: '40px',
                    }}
                  >
                    Move here
                  </Button>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={handleClose}
          sx={{ 
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          sx={{
            backgroundColor: '#337A5C',
            '&:hover': {
              backgroundColor: '#2a6350',
            },
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
          }}
        >
          {isLoading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
              {mode === 'edit' ? 'Saving...' : 'Creating...'}
            </>
          ) : (
            mode === 'edit' ? 'Save Changes' : 'Create Company'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewCompanyModal;
